import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FloatingPortal, autoUpdate, flip, offset, shift, useDismiss, useFloating, useInteractions, useTransitionStyles } from '@floating-ui/react';
import { useCopyToClipboard } from '@uidotdev/usehooks';
import { useLiveQuery } from 'dexie-react-hooks';
import { ChevronLeft, QrCode } from 'lucide-react';

import { apiPost, apiPostAsUser } from '../../lib/api_v2';
import { buildChannelShareUrl } from '../../lib/channels';
import db, { DEXIE_IS_LOADING } from '../../lib/db';
import { logError } from '../../lib/logger';
import { cn } from '../../lib/utils';

import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import PeoplePickerPanel from '../shared/PeoplePickerPanel';
import ProfileChiclet from '../shared/ProfileChiclet';
import QrCodePanel from '../shared/QrCodePanel';
import { TOOLTIP_BUBBLE_CLASS } from '../shared/Tooltip';
import { SETTINGS_MODAL_CLOSE_BUTTON_CLASS, SETTINGS_MODAL_CLOSE_ICON } from '../shared/settingsModalChrome';
import { Modal } from './Modal';
import { ModalBody } from './ModalBody';

const MAX_INVITE_COUNT = 25;
const COPY_FEEDBACK_MS = 1500;
const channelDexieKey = (workspaceId, channelId) => [String(workspaceId), String(channelId)];

function mutualChatSubtitle(user) {
  const count = Number(user.shared_chat_count) || 0;
  if (count === 1) return '1 mutual';
  if (count > 1) return `${count} mutuals`;
  return user.blurb;
}

const InviteLinkCard = ({ shareUrl, isAdmin, isResetting, didCopy, onReset, onCopy }) => {
  // The picker body is overflow-y-auto, so the "Copied" helper must portal out or it clips.
  const { refs, floatingStyles } = useFloating({
    open: didCopy,
    placement: 'top',
    middleware: [offset(6), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  return (
    <div className="flex items-center gap-2.5 rounded-lg border-[0.5px] border-white bg-gray-50 p-4">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="truncate text-sm leading-5 font-medium text-black">Invite link</span>
        <span className="truncate text-xs leading-4 text-gray-400 lining-nums proportional-nums">{shareUrl}</span>
      </div>
      <div className="flex shrink-0 items-start gap-2">
        {isAdmin && (
          <button
            type="button"
            data-app-region="none"
            onClick={onReset}
            disabled={isResetting}
            className="flex h-9 cursor-pointer items-center justify-center rounded-full border border-gray-300 px-3 text-cursed text-gray-600 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            Reset
          </button>
        )}
        <button
          ref={refs.setReference}
          type="button"
          data-app-region="none"
          onClick={onCopy}
          className="flex h-9 cursor-pointer items-center justify-center rounded-full bg-gray-800 px-3 text-cursed text-white hover:bg-gray-900"
        >
          Copy
        </button>
        {didCopy && (
          <FloatingPortal>
            <div ref={refs.setFloating} style={floatingStyles} className="pointer-events-none z-(--stack-tooltip)">
              <div className={cn(TOOLTIP_BUBBLE_CLASS, 'whitespace-nowrap')}>
                Copied
              </div>
            </div>
          </FloatingPortal>
        )}
      </div>
    </div>
  );
};

const InvitePeopleModal = ({ onClose, onBack, initialSelectedUser = null }) => {
  const { workspace, workspaceId, mainChannelId, profile } = useRouteWorkspace();
  const isAdmin = profile?.role === 'admin';
  // Keyed by user id, holding the full user so selections can render as chiclets.
  const [pickerState, setPickerState] = useState(() => ({
    selectedUsers: initialSelectedUser ? new Map([[initialSelectedUser.id, initialSelectedUser]]) : new Map(),
    errorMessage: null,
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [didCopy, setDidCopy] = useState(false);
  const [isQrOpen, setIsQrOpen] = useState(false);
  const [, copyToClipboard] = useCopyToClipboard();
  const isMountedRef = useRef(true);
  const { selectedUsers, errorMessage } = pickerState;

  const selectedUserIds = useMemo(() => new Set(selectedUsers.keys()), [selectedUsers]);

  const mainChannelResult = useLiveQuery(
    () => {
      if (!workspaceId || !mainChannelId) return null;
      return db.channels.get(channelDexieKey(workspaceId, mainChannelId));
    },
    [mainChannelId, workspaceId],
    DEXIE_IS_LOADING,
  );
  const mainChannel = mainChannelResult === DEXIE_IS_LOADING ? null : mainChannelResult;
  const shareLinkEnabled = Boolean(mainChannel?.share_key && !mainChannel?.share_disabled_at);
  const shareUrl = shareLinkEnabled && workspace ? buildChannelShareUrl(workspace, mainChannel) : '';

  const setErrorMessage = useCallback(function updateInviteErrorMessage(nextErrorMessage) {
    setPickerState((currentState) => ({ ...currentState, errorMessage: nextErrorMessage }));
  }, []);

  const handleToggleUser = useCallback(function toggleInviteUser(user) {
    setPickerState((currentState) => {
      const nextUsers = new Map(currentState.selectedUsers);
      if (!nextUsers.has(user.id) && nextUsers.size >= MAX_INVITE_COUNT) {
        return { ...currentState, errorMessage: `You can invite up to ${MAX_INVITE_COUNT} people at once.` };
      }

      if (nextUsers.has(user.id)) nextUsers.delete(user.id);
      else nextUsers.set(user.id, user);
      return { selectedUsers: nextUsers, errorMessage: null };
    });
  }, []);

  const handleCopyShareUrl = useCallback(() => {
    if (!shareUrl) return;
    copyToClipboard(shareUrl);
    setDidCopy(true);
    setTimeout(() => setDidCopy(false), COPY_FEEDBACK_MS);
  }, [copyToClipboard, shareUrl]);

  const handleResetShareUrl = useCallback(async () => {
    if (!isAdmin || !mainChannel?.id) return;
    if (!window.confirm('Reset the invite link? The current link will stop working.')) return;

    setIsResetting(true);
    try {
      const response = await apiPost(`/channels/${mainChannel.id}/reset_share_key`);
      const data = await response.json();
      if (data.ok && data.data?.channel) {
        const updatedChannel = data.data.channel;
        await db.channels.update(channelDexieKey(updatedChannel.workspace_id, updatedChannel.id), updatedChannel);
      } else {
        logError('InvitePeopleModal', 'Failed to reset share key', { errors: data.errors });
      }
    } catch (error) {
      logError('InvitePeopleModal', 'Failed to reset share key', { error });
    } finally {
      if (isMountedRef.current) setIsResetting(false);
    }
  }, [isAdmin, mainChannel?.id]);

  // QR opens as an anchored menu-style popover, not a separate modal
  const {
    refs: qrRefs,
    floatingStyles: qrFloatingStyles,
    context: qrContext,
  } = useFloating({
    open: isQrOpen,
    onOpenChange: setIsQrOpen,
    placement: 'bottom-end',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const qrDismiss = useDismiss(qrContext);
  const { getReferenceProps: getQrReferenceProps, getFloatingProps: getQrFloatingProps } = useInteractions([qrDismiss]);
  const { isMounted: isQrMounted, styles: qrTransitionStyles } = useTransitionStyles(qrContext, {
    duration: { open: 180, close: 100 },
    initial: { opacity: 0, transform: 'scale(0.9)' },
    common: { transformOrigin: 'top right', transitionTimingFunction: 'var(--ease-fluid)' },
  });
  const handleToggleQr = useCallback(() => setIsQrOpen((prev) => !prev), []);

  const selectedCount = selectedUsers.size;

  const handleSubmit = useCallback(
    async function inviteSelectedPeople() {
      if (!workspaceId || selectedUsers.size === 0 || isSubmitting) return;

      setIsSubmitting(true);
      setErrorMessage(null);

      try {
        const response = await apiPostAsUser(`/workspaces/${workspaceId}/members`, {
          members: { user_ids: Array.from(selectedUsers.keys()) },
        });
        const data = await response.json();
        const profiles = data.data?.profiles;

        if (!(response.ok && data.ok && Array.isArray(profiles))) {
          throw new Error(`Failed to invite people: ${response.status}`);
        }

        try {
          await db.profiles.bulkPut(profiles);
        } catch (cacheError) {
          logError('InvitePeopleModal', 'Failed to cache invited profiles', cacheError);
        }

        onClose();
      } catch (error) {
        logError('InvitePeopleModal', 'Failed to invite people', error);
        if (isMountedRef.current) setErrorMessage('Could not invite those people. Please try again.');
      } finally {
        if (isMountedRef.current) setIsSubmitting(false);
      }
    },
    [isSubmitting, onClose, selectedUsers, setErrorMessage, workspaceId],
  );

  useEffect(
    function resetSelectionForWorkspace() {
      // Reopening from a suggested face re-seeds that person rather than clearing to empty.
      setPickerState({
        selectedUsers: initialSelectedUser ? new Map([[initialSelectedUser.id, initialSelectedUser]]) : new Map(),
        errorMessage: null,
      });
    },
    [initialSelectedUser, workspaceId],
  );

  useEffect(function trackMountedState() {
    isMountedRef.current = true;
    return function markUnmounted() {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <Modal
      // With onBack (opened from the settings modal) the chrome button is a back chevron
      // returning there; without it (opened directly) it's an X that closes. Escape and
      // outside-press always fully close via the portal's own onClose.
      onClose={onBack || onClose}
      className="rounded-2xl"
      closePlacement="top-left"
      showClose={{ desktop: true, mobile: true }}
      padding="none"
      closeButtonClassName={SETTINGS_MODAL_CLOSE_BUTTON_CLASS}
      closeIcon={
        onBack ? (
          <ChevronLeft className="pointer-events-none size-5.5 -translate-x-px" strokeWidth={1.75} absoluteStrokeWidth />
        ) : (
          SETTINGS_MODAL_CLOSE_ICON
        )
      }
    >
      <ModalBody padding="none" className="h-full overflow-hidden">
        <div className="relative mx-5 mt-5 flex h-9 shrink-0 items-center justify-center">
          <h1 className="max-w-full truncate px-24 text-center text-base leading-5 font-medium text-black dark:text-neutral-200">
            Add Members
          </h1>
          <div className="absolute right-0 flex items-center gap-2">
            {shareUrl && (
              <button
                ref={qrRefs.setReference}
                {...getQrReferenceProps()}
                type="button"
                data-app-region="none"
                onClick={handleToggleQr}
                aria-label="Show QR code"
                className="flex size-9 cursor-pointer items-center justify-center rounded-full border-[0.5px] border-white bg-gray-50 text-black shadow-[0px_4px_4px_rgba(0,0,0,0.02)] hover:bg-white dark:border-white/10 dark:bg-neutral-900 dark:hover:bg-neutral-800"
              >
                <QrCode className="size-4.5" strokeWidth={2} absoluteStrokeWidth />
              </button>
            )}
            <button
              type="button"
              data-app-region="none"
              onClick={handleSubmit}
              disabled={!workspaceId || selectedCount === 0 || isSubmitting}
              className="flex h-9 w-15 cursor-pointer items-center justify-center rounded-full bg-idle-blue text-sm leading-[18px] font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? '...' : 'Add'}
            </button>
          </div>
        </div>

        <PeoplePickerPanel
          workspaceId={workspaceId}
          leadingContent={
            shareUrl ? (
              <InviteLinkCard
                shareUrl={shareUrl}
                isAdmin={isAdmin}
                isResetting={isResetting}
                didCopy={didCopy}
                onReset={handleResetShareUrl}
                onCopy={handleCopyShareUrl}
              />
            ) : null
          }
          aboveListContent={
            <div
              // 0fr/1fr grid rows animate the height jump when the chiclet section appears or collapses.
              // -mx-5 lives out here, outside the overflow-hidden, so the strip truly reaches the modal edges.
              className={cn(
                '-mx-5 grid transition-[grid-template-rows] duration-200 ease-fluid',
                selectedCount > 0 ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
              )}
            >
              <div className="overflow-hidden">
                {/* single horizontal strip, flush to the modal edges, h-scrollable with hidden scrollbar */}
                <div className="scrollbar-hidden flex items-center gap-2 overflow-x-auto px-5 pb-5">
                  {Array.from(selectedUsers.values()).map((user) => (
                    <ProfileChiclet key={user.id} user={user} onRemove={handleToggleUser} disabled={isSubmitting} />
                  ))}
                </div>
              </div>
            </div>
          }
          selectionMode="multiple"
          selectedUserIds={selectedUserIds}
          onToggleUser={handleToggleUser}
          getUserSubtitle={mutualChatSubtitle}
          disabled={isSubmitting}
          externalErrorMessage={errorMessage}
          emptySuggestedMessage="No more suggested people"
        />
      </ModalBody>

      {isQrMounted && shareUrl && (
        <FloatingPortal>
          <div
            ref={qrRefs.setFloating}
            style={qrFloatingStyles}
            {...getQrFloatingProps()}
            className="z-(--stack-modal-dropdown)"
          >
            <div
              style={qrTransitionStyles}
              className="flex items-center justify-center rounded-2xl border-[0.5px] border-white bg-white/50 p-2 shadow-[0px_0px_20px_0px_rgba(0,0,0,0.32)] backdrop-blur-[10px]"
            >
              <div className="w-30">
                <QrCodePanel url={shareUrl} theme="light" />
              </div>
            </div>
          </div>
        </FloatingPortal>
      )}
    </Modal>
  );
};

export default InvitePeopleModal;
